import humanFileSize from "./humanFileSize";

interface SessionStats {
  sessionStart: number;
  totalInstances: number;
  totalFrames: number;
  totalCompressedBytes: number;
  totalDecompressedBytes: number;
  peakHeapBytes: number;
  peakHeapLimitBytes: number;
  peakOPFSBytes: number;
  opfsQuotaBytes: number;
  // Image cache. The 512 MB cap silently becoming unbounded is what turned a
  // recoverable storage failure into an OOM crash, and none of it showed up in
  // the report — so the cap's history is now first-class.
  cacheLimitBytes: number;
  minCacheLimitBytes: number;
  maxCacheLimitBytes: number;
  peakCacheBytes: number;
  peakCacheImages: number;
  /**
   * Peak bytes of parsed source files held resident. Shares one budget with the
   * frame cache, and for a single large multiframe instance it is the dominant
   * term — without it the report can't explain where the heap went.
   */
  peakSourceResidentBytes: number;
  // Pixel sourcing
  decodesFromSource: number;
  sourceReadFailures: number;
  /**
   * How the session's instance sources are held. `memory` is the one that costs
   * heap for the whole session: bytes handed to us with no file behind them
   * (DICOMweb) that we then failed to spool to OPFS. A non-zero count here is
   * the signature of a study that will grow the heap as it loads.
   */
  sources: {
    file: { count: number; bytes: number };
    opfs: { count: number; bytes: number };
    memory: { count: number; bytes: number };
  };
  // OPFS
  opfsWriteFailures: { quota: number; lock: number; other: number };
  opfsBackoffEvents: number;
  orphanDirsRemoved: number;
}

const stats: SessionStats = {
  sessionStart: Date.now(),
  totalInstances: 0,
  totalFrames: 0,
  totalCompressedBytes: 0,
  totalDecompressedBytes: 0,
  peakHeapBytes: 0,
  peakHeapLimitBytes: 0,
  peakOPFSBytes: 0,
  opfsQuotaBytes: 0,
  cacheLimitBytes: 0,
  minCacheLimitBytes: Number.MAX_SAFE_INTEGER,
  maxCacheLimitBytes: 0,
  peakCacheBytes: 0,
  peakCacheImages: 0,
  peakSourceResidentBytes: 0,
  decodesFromSource: 0,
  sourceReadFailures: 0,
  sources: {
    file: { count: 0, bytes: 0 },
    opfs: { count: 0, bytes: 0 },
    memory: { count: 0, bytes: 0 },
  },
  opfsWriteFailures: { quota: 0, lock: 0, other: 0 },
  opfsBackoffEvents: 0,
  orphanDirsRemoved: 0,
};

export function recordInstanceLoaded(args: {
  compressedBytes: number;
  decompressedBytes: number;
  frameCount: number;
}) {
  stats.totalInstances += 1;
  stats.totalFrames += args.frameCount;
  stats.totalCompressedBytes += args.compressedBytes;
  stats.totalDecompressedBytes += args.decompressedBytes;
}

export function recordHeapUsage(bytes: number, limitBytes = 0) {
  if (bytes > stats.peakHeapBytes) stats.peakHeapBytes = bytes;
  if (limitBytes > stats.peakHeapLimitBytes) stats.peakHeapLimitBytes = limitBytes;
}

export function recordOPFSUsage(usageBytes: number, quotaBytes: number) {
  if (usageBytes > stats.peakOPFSBytes) stats.peakOPFSBytes = usageBytes;
  if (quotaBytes > stats.opfsQuotaBytes) stats.opfsQuotaBytes = quotaBytes;
}

/** Every change to the cornerstone image cache ceiling. */
export function recordCacheLimit(bytes: number) {
  stats.cacheLimitBytes = bytes;
  if (bytes < stats.minCacheLimitBytes) stats.minCacheLimitBytes = bytes;
  if (bytes > stats.maxCacheLimitBytes) stats.maxCacheLimitBytes = bytes;
}

export function recordCacheSnapshot(sizeBytes: number, imageCount: number) {
  if (sizeBytes > stats.peakCacheBytes) stats.peakCacheBytes = sizeBytes;
  if (imageCount > stats.peakCacheImages) stats.peakCacheImages = imageCount;
}

/** Total bytes of parsed source files currently held resident. */
export function recordSourceResident(bytes: number) {
  if (bytes > stats.peakSourceResidentBytes) {
    stats.peakSourceResidentBytes = bytes;
  }
}

/** A frame that had to be decoded from its source file (i.e. an image cache miss). */
export function recordDecodeFromSource() {
  stats.decodesFromSource += 1;
}

export function recordSourceReadFailure() {
  stats.sourceReadFailures += 1;
}

/** How one instance's source ended up being held. See `SessionStats.sources`. */
export function recordSourceRegistered(
  kind: "file" | "opfs" | "memory",
  bytes: number
) {
  stats.sources[kind].count += 1;
  stats.sources[kind].bytes += bytes;
}

export function recordOPFSWriteFailure(kind: "quota" | "lock" | "other") {
  stats.opfsWriteFailures[kind] += 1;
}

export function recordOPFSBackoff() {
  stats.opfsBackoffEvents += 1;
}

export function recordOrphanScrub(removed: number) {
  stats.orphanDirsRemoved += removed;
}

export function getSessionStats(): Readonly<SessionStats> {
  return stats;
}

function formatDuration(ms: number): string {
  const totalSec = Math.round(ms / 1000);
  const h = Math.floor(totalSec / 3600);
  const m = Math.floor((totalSec % 3600) / 60);
  const s = totalSec % 60;
  if (h > 0) return `${h}h ${m}m ${s}s`;
  if (m > 0) return `${m}m ${s}s`;
  return `${s}s`;
}

function withRaw(bytes: number): string {
  return `${humanFileSize(bytes)} (${bytes.toLocaleString()} bytes)`;
}

export function buildSessionReport(): string {
  const elapsedMs = Date.now() - stats.sessionStart;
  const avgCompressed =
    stats.totalFrames > 0
      ? stats.totalCompressedBytes / stats.totalFrames
      : 0;
  const avgDecompressed =
    stats.totalFrames > 0
      ? stats.totalDecompressedBytes / stats.totalFrames
      : 0;

  const appVersion =
    typeof (globalThis as any).__APP_VERSION__ !== "undefined"
      ? (globalThis as any).__APP_VERSION__
      : "unknown";

  const lines: string[] = [
    "─── Pacsbin Uploader Diagnostic Report ───",
    `Generated:              ${new Date().toISOString()}`,
    `Session duration:       ${formatDuration(elapsedMs)}`,
    `App version:            ${appVersion}`,
    `User agent:             ${navigator.userAgent}`,
    `Platform:               ${navigator.platform}`,
    `Hardware concurrency:   ${navigator.hardwareConcurrency ?? "unknown"}`,
    `Device memory (GB):     ${(navigator as any).deviceMemory ?? "unknown"}`,
    "",
    `Instances loaded:       ${stats.totalInstances.toLocaleString()}`,
    `Total frames:           ${stats.totalFrames.toLocaleString()}`,
    `Total compressed:       ${withRaw(stats.totalCompressedBytes)}`,
    `Total decompressed:     ${withRaw(stats.totalDecompressedBytes)}`,
    `Avg frame (compressed): ${withRaw(Math.round(avgCompressed))}`,
    `Avg frame (decompr.):   ${withRaw(Math.round(avgDecompressed))}`,
    "",
    `Peak JS heap:           ${
      stats.peakHeapBytes > 0
        ? withRaw(stats.peakHeapBytes)
        : "unavailable (performance.memory not supported)"
    }`,
    `Heap limit:             ${
      stats.peakHeapLimitBytes > 0 ? withRaw(stats.peakHeapLimitBytes) : "unknown"
    }`,
    `Peak heap / limit:      ${
      stats.peakHeapLimitBytes > 0
        ? `${((stats.peakHeapBytes / stats.peakHeapLimitBytes) * 100).toFixed(1)}%`
        : "unknown"
    }`,
    "",
    `Image cache limit:      ${withRaw(stats.cacheLimitBytes)}`,
    `  (budget shared with resident source files — see below)`,
    `  min / max ever set:   ${
      stats.minCacheLimitBytes === Number.MAX_SAFE_INTEGER
        ? "n/a"
        : humanFileSize(stats.minCacheLimitBytes)
    } / ${humanFileSize(stats.maxCacheLimitBytes)}`,
    `  cap ever unbounded:   ${
      stats.maxCacheLimitBytes >= Number.MAX_SAFE_INTEGER ? "YES — see logs" : "no"
    }`,
    `Peak image cache:       ${withRaw(
      stats.peakCacheBytes
    )} across ${stats.peakCacheImages.toLocaleString()} images`,
    `Peak source resident:   ${withRaw(stats.peakSourceResidentBytes)}`,
    `Decodes from source:    ${stats.decodesFromSource.toLocaleString()}`,
    `Source read failures:   ${stats.sourceReadFailures.toLocaleString()}`,
    `Sources held as files:  ${stats.sources.file.count.toLocaleString()} (${humanFileSize(
      stats.sources.file.bytes
    )}, disk-backed)`,
    `Sources spooled (OPFS): ${stats.sources.opfs.count.toLocaleString()} (${humanFileSize(
      stats.sources.opfs.bytes
    )})`,
    `Sources held in memory: ${stats.sources.memory.count.toLocaleString()} (${humanFileSize(
      stats.sources.memory.bytes
    )})${stats.sources.memory.count > 0 ? " — spooling failed, see OPFS below" : ""}`,
    "",
    `Peak OPFS usage:        ${
      stats.peakOPFSBytes > 0
        ? withRaw(stats.peakOPFSBytes)
        : "unavailable or unused"
    }`,
    `OPFS quota observed:    ${
      stats.opfsQuotaBytes > 0 ? withRaw(stats.opfsQuotaBytes) : "unavailable"
    }`,
    `OPFS write failures:    quota=${stats.opfsWriteFailures.quota} lock=${stats.opfsWriteFailures.lock} other=${stats.opfsWriteFailures.other}`,
    `OPFS backoff events:    ${stats.opfsBackoffEvents}`,
    `Orphan dirs reclaimed:  ${stats.orphanDirsRemoved}`,
    "──────────────────────────────────────────",
  ];

  return lines.join("\n");
}

export async function logSessionReport(): Promise<string> {
  const report = buildSessionReport();
  console.log(report);
  try {
    if (navigator.clipboard?.writeText) {
      await navigator.clipboard.writeText(report);
      console.log(
        "%c(Report copied to clipboard)",
        "font-style:italic;color:#3DD0FC"
      );
    }
  } catch {
    // clipboard may be blocked; report is still in console
  }
  return report;
}

if (typeof window !== "undefined") {
  (window as any).pacsbinSessionReport = logSessionReport;
}
